import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BodyForm,
  InputsWrapper,
  PasswordWrapper,
  ErrorContainer,
  ErrorText,
  FormWrapper,
  FieldGroup,
  FieldLabel,
  FieldError,
  RequiredMark,
  FlavorGrid,
  FlavorCard,
  SelectedCheck,
  FlavorName,
  FlavorDivider,
  FlavorMeta,
  SourceControl,
  SourceTabs,
  SourceTab,
  OsGrid,
  OsCard,
  OsIcon,
  OsName,
  HiddenMarketplace,
  MarketplaceMockGrid,
  MarketplaceMockCard,
  ControlsRow,
  ButtonRow,
} from './styles'
import * as yup from 'yup';
import { useFormik } from 'formik';
import {
  Input,
  Select,
  Counter,
  Button,
  ModalVariables,
  TopProgress,
  MatchPassword,
  MultiInput,
} from "../../../../../components";
import { useQueryParam, StringParam } from 'use-query-params';
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../../../../hooks/useLocalStorage";
import { useAuth } from "../../../../../context/user.context";
import ContentLoader from 'react-content-loader'
import { useTheme } from "styled-components";
import { useInstance } from "../../../../../context/instanceContext";
import { instanceService } from "../../../../../api";
import ErrorIc from './ic-error.svg'
import { icons } from "../../../../../icons";
import { isFlavorCompatibleWithBootSource } from "./flavorCompatibility";

const SHOW_MARKETPLACE_LAYOUT = false

const formatOsName = (source:any) => {
  const distro = source?.os_distro || ''
  const names:any = {
    almalinux: 'AlmaLinux',
    centos: 'CentOS',
    cirros: 'Cirros',
    debian: 'Debian',
    rockylinux: 'Rocky Linux',
    ubuntu: 'Ubuntu',
    windows: 'Windows',
  }

  return names[distro] || source?.label?.split(' ')?.[0] || source?.name || distro
}


const Configure:FC = () => {
  const {user, quotas} = useAuth()
  const {setDataToLs, getDataToLs, removeItemFromLs} = useLocalStorage()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [activeConfirm, setActiveConfirm] = useState<boolean>(false)
  const [activeButton, setActiveButton] = useState<boolean>(false)
  const [typeValid, setTypeValid] = useState<any>()
  const [storageTypeLocal, setStorageType] = useState<any>()
  const [sourceTab, setSourceTab] = useState<'os' | 'marketplace' | 'client'>('os')
  const [machines, setMachines] = useState<any>(1)
  const [tags, setTags] = useState<string[]>(getDataToLs({key:'instance.configure'})?.tags || [])
  const InputRef:any = useRef()

  const {
    configureData,
    setConfigureData,
    bootSource,
    flavors,
    regions,
    storageType,
    pageLoading,
    setNetworkData,
  } = useInstance()
  const closeModal = (isSave?:boolean) => {
    setViewModal(false)
    setActiveAnimation(false)
    if(!isSave){
      removeItemFromLs('instance.configure')
      removeItemFromLs('instance.network')
      setConfigureData(null)
      setNetworkData(null)
    }
    navigate("/instances")
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const openModal = () => {
    setViewModal(true)
    setActiveAnimation(true)
  }


  const navigate = useNavigate()
  const [t] = useTranslation()
  const [step, setStep] = useQueryParam('step', StringParam);
  const [initialValues, setInitialValues] = useState<any>({
    name:getDataToLs({key:'instance.configure'})?.name,
    type:getDataToLs({key:'instance.configure'})?.type,
    region:getDataToLs({key:'instance.configure'})?.region||regions?.[0]?.value,
    os:getDataToLs({key:'instance.configure'})?.os,
    diskType:getDataToLs({key:'instance.configure'})?.disk || '1',
    root_password:getDataToLs({key:'instance.configure'})?.root_password || '',
    tag:getDataToLs({key:'instance.configure'})?.tag || '',
    countMachines:getDataToLs({key:'instance.configure'})?.countMachines || '1',
    storageType:'local',
    volumeSize:10,
    // root_password:''
  }) 
  const onSubmit = (values:any) => {
    setConfigureData({...values, tags:tags})
    setStep("network")
  }
  const maxCountInstances = (quotas:any, value?:any) => {
    const flavorObj = flavors.find((flav:any) => flav.id === typeValid)
    if(!flavorObj){
      return quotas?.core?.instances?.limit - quotas?.core?.instances?.in_use
    }
    let maxCores = (quotas?.core?.cores?.limit - quotas?.core?.cores?.in_use) / flavorObj?.vcpus
    let maxRam = (quotas?.core?.ram?.limit - quotas?.core?.ram?.in_use) / flavorObj?.memory_mb
    let maxInstances = quotas?.core?.instances?.limit - quotas?.core?.instances?.in_use
    return Math.min(Math.floor(maxCores), Math.floor(maxRam), Math.floor(maxInstances) )
  }
  const regLetter = /^(?=.*[a-z])(?=.*[A-Z])/
  const regNumbers = /^(?=.*[0-9])/
  const regSymbols = /^(?=.*[ !#$@%&'()*+?><,./[\\\]^_`{|}~"-])/
  const allowedSymbols = ` !#$@%&'()*+?><,-./[\\]^_\`{|}~"`
  const regName = /^[a-zA-Z0-9\\!\"#$%&'()*+,\-./:;<=>?@\[\\\]\^_`{\|}~ ]+$/
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string()
      .required(`${t('REQUIRED_FIELD')}`)
      .matches(regName,`${t("ONLY_ENGLISH_LETTERS")}`)
      .max(60, `${t("STRING_VALIDATE_MAX")} 60 ${t("CHARACTERS")}`),
      type: yup.string()
        .required(`${t('REQUIRED_FIELD')}`)
        .oneOf(flavors.map((flavor:any) => flavor.id || flavor.value), `${t('REQUIRED_FIELD')}`),
      os: yup.string()
        .required(`${t('REQUIRED_FIELD')}`)
        .oneOf(bootSource.map((source:any) => source.value), `${t('REQUIRED_FIELD')}`),
      diskType: yup.string(),
      // .when("storageType", {
      //   is: (storageType:any) => storageType !== 'local',
      //   then: yup.string().required()
      // }),
      root_password:yup.string()
        .required(`${t('REQUIRED_FIELD')}`)
        .min(8, `${t("PASSWORD_DID_NOT_PASS")}`)
        .max(24, `${t("PASSWORD_DID_NOT_PASS")}`)
        .matches(regLetter, `${t("PASSWORD_DID_NOT_PASS")}`)
        .matches(regNumbers, `${t("PASSWORD_DID_NOT_PASS")}`)
        .matches(regSymbols, `${t("PASSWORD_SPECIAL_CHAR_REQUIRED")}: ${allowedSymbols}`),
      storageType:yup.string().required(`${t('REQUIRED_FIELD')}`),
      countMachines: yup.number()
        .required(`${t('REQUIRED_FIELD')}`)
        .positive()
        .integer()
        .min(1, `${t("MIN_COUNT_NUMBER")} 1`),
        // .max(maxCountInstances(quotas), `${t("MAX_COUNT_MACHINES")} ${maxCountInstances(quotas)}`),
      volumeSize: storageTypeLocal === 'local' ?
        yup.number()
        .positive()
        .integer() :
        yup.number().when("storageType", {
          is: (storageType:any) => storageType !== 'local',
          then: yup.number().required(`${t('REQUIRED_FIELD')}`)
          .positive()
          .integer()
          .min(flavors.find((flav:any) => flav.id === typeValid)?.root_gb || '10', `${t("SIZE_MUST_BE_GREATER_OR_EQUAL")} ${flavors.find((flav:any) => flav.id === typeValid)?.root_gb || '10'}GB`),
          // .max((quotas?.volume?.gigabytes?.limit - quotas?.volume?.gigabytes?.in_use) / machines, `${t("SIZE_MUST_BE_LESS_THAN_OR_EQUAL")} ${Math.floor((quotas?.volume?.gigabytes?.limit - quotas?.volume?.gigabytes?.in_use) / machines)} GB`),
      }),
      tag:yup.string(),
      region:yup.string().required(`${t('REQUIRED_FIELD')}`),
    }),
    initialValues: {
      ...initialValues,
    },
    onSubmit,
  });
  const changeTags = (tag:string) => {
    const founded = tags.find((item:any) => item === tag)
    if(founded || tag === '' || tag === undefined) {
      setTags((prev:any) => {
        return prev.filter((item:any) => item !== tag)
      })
    } else {
      setTags((prev) => [...prev, tag])
      handleChange('tag')('')
    }
  }
  useEffect(() => {
    setMachines(values['countMachines'])
  },[values['countMachines']])
  
  useEffect(() => {
    setInitialValues({
      ...initialValues,
      region:getDataToLs({key:'instance.configure'})?.region||regions?.[0]?.value,
    })
  },[regions])
  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };
  useEffect(() => {
    if(typeValid && flavors?.length > 0){
      handleChange('volumeSize')(flavors?.find((flav:any) => flav.id === typeValid)?.root_gb.toString())
    } 
  },[typeValid])
  useEffect(() => {
    setDataToLs({key:'instance.configure', data:{...values, tags:tags}})
    setConfigureData({...values, tags:tags})
  },[values])
  const theme:any = useTheme()
  useEffect(() => {
    let ignoreResponse = false

    if(!pageLoading && values['os'] && values['type']){
      const selectedFlavor = flavors.find((flavor:any) => flavor.id === values['type'] || flavor.value === values['type'])
      const selectedImage = bootSource.find((source:any) => source.value === values['os'])

      if(selectedFlavor && !isFlavorCompatibleWithBootSource(selectedFlavor, selectedImage)){
        handleChange('type')('')
        return
      }

      instanceService.getFlavorsAssignedToImage({
        image_id:values['os'],
        selected_client_id:user.user.active_client
      })
      .then((flavs:any) => {
        if(!ignoreResponse && flavs?.data?.flavors?.find((f:any) => f.id === values['type']) === undefined){
          handleChange('type')('')
        }
      })
      .catch(() => null)
    }

    return () => {
      ignoreResponse = true
    }
  },[pageLoading, values['os'], values['type'], user.user.active_client, bootSource, flavors])
  // useEffect(() => {
  //   if(!activeButton){
  //     setErrors({...errors, root_password:''})
  //   }
  // },[activeConfirm])
  
  useEffect(() => {
    setTypeValid(values['type'])
  },[values['type']])
  useEffect(() => {
    setStorageType(values['storageType'])
  },[values['storageType']])

  const selectedBootSource = bootSource.find((boot:any) => boot.value === values['os'])
  const publicBootSource = bootSource.filter((boot:any) => boot.category === 'public')
  const clientBootSource = bootSource.filter((boot:any) => boot.category === 'client')
  const osGroups = publicBootSource.reduce((acc:any[], source:any) => {
    const groupKey = source.os_distro || source.label
    const existing = acc.find((item:any) => item.key === groupKey)

    if(existing){
      existing.sources.push(source)
      return acc
    }

    return [...acc, {
      key: groupKey,
      title: formatOsName(source),
      icon: source.icon,
      sources: [source]
    }]
  }, [])
  const selectedOsGroup = osGroups.find((group:any) => group.sources.some((source:any) => source.value === values['os']))
  const selectedOsVersions = selectedOsGroup?.sources || []
  const marketplaceTab = SHOW_MARKETPLACE_LAYOUT ? [{id:'marketplace', title:t('MARKETPLACE')}] : []
  const sourceTabs = [
    {id:'os', title:t('OS')},
    ...marketplaceTab,
    {id:'client', title:t('USER_IMAGES')},
  ]

  useEffect(() => {
    if(selectedBootSource?.category === 'client'){
      setSourceTab('client')
    }
  },[selectedBootSource?.category])
  
  if(pageLoading) return (
    <>
      <TopProgress loading={pageLoading}/>
      <ContentLoader backgroundColor={theme.palette[10]} viewBox="0 0 500 800" height={800} width={500}>
        <rect x="0" y="45" rx="4" ry="4" width="343" height="48" />
        <rect x="0" y="125" rx="4" ry="4" width="343" height="48" />
        <rect x="0" y="205" rx="4" ry="4" width="343" height="48" />
        <rect x="0" y="285" rx="4" ry="4" width="343" height="48" />
        {/* <rect x="0" y="335" rx="4" ry="4" width="543" height="308" /> */}
        <rect x="0" y="365" rx="4" ry="4" width="120" height="40" />
        <rect x="100" y="445" rx="4" ry="4" width="80" height="40" />
        <rect x="0" y="445" rx="4" ry="4" width="80" height="40" />
      </ContentLoader>
    </>
  )
  return (
    <BodyForm data-test-id='instance-configure-container'>
      <FormWrapper>
        <InputsWrapper>
          <Input
            required
            fieldName='name'
            title={t("NAME_OF_INSTANCE")}
            placeholder=''
            {...formikProps}
          />
        </InputsWrapper>

        <FieldGroup
          tabIndex={touched['type'] && errors['type'] ? -1 : undefined}
          data-validation-field='type'
          data-validation-error={touched['type'] && errors['type'] ? true : undefined}
        >
          <FieldLabel>{t("TYPE_OF_INSTANCE")}<RequiredMark>*</RequiredMark></FieldLabel>
          <FlavorGrid data-test-id='instance-type-select'>
            {flavors.map((flavor:any) => {
              const isActive = values['type'] === flavor.id || values['type'] === flavor.value
              const isCompatible = isFlavorCompatibleWithBootSource(flavor, selectedBootSource)
              const memory = flavor.memory_gb || (flavor.memory_mb ? Math.round(flavor.memory_mb / 1024) : '')
              const price = flavor.customItem?.estimate || ''

              return (
                <FlavorCard
                  key={flavor.id || flavor.value}
                  type='button'
                  active={isActive}
                  aria-disabled={!isCompatible}
                  aria-label={!isCompatible ? `${flavor.name || flavor.label}. ${t("INCOMPATIBLE_FLAVOR")}` : undefined}
                  title={!isCompatible ? t("INCOMPATIBLE_FLAVOR") : undefined}
                  onClick={() => {
                    if(isCompatible){
                      handleChange('type')(flavor.id || flavor.value)
                    }
                  }}
                >
                  {isActive && <SelectedCheck />}
                  <FlavorName>{flavor.name || flavor.label}</FlavorName>
                  <FlavorDivider />
                  <FlavorMeta>{flavor.vcpus} CPU&nbsp;&nbsp;{memory}GB RAM&nbsp;&nbsp;{price}</FlavorMeta>
                </FlavorCard>
              )
            })}
          </FlavorGrid>
          {touched['type'] && errors['type'] &&
            <FieldError>{`${errors['type']}`}</FieldError>
          }
        </FieldGroup>

        <FieldGroup
          tabIndex={touched['os'] && errors['os'] ? -1 : undefined}
          data-validation-field='os'
          data-validation-error={touched['os'] && errors['os'] ? true : undefined}
        >
          <SourceControl>
            <FieldLabel>{t("OS")}<RequiredMark>*</RequiredMark></FieldLabel>
            <SourceTabs>
              {sourceTabs.map((tab:any) => (
                <SourceTab
                  key={tab.id}
                  type='button'
                  active={sourceTab === tab.id}
                  onClick={() => setSourceTab(tab.id)}
                >
                  {tab.title}
                </SourceTab>
              ))}
            </SourceTabs>
          </SourceControl>

          {sourceTab === 'os' &&
            <>
              <OsGrid data-test-id='instance-os-select'>
                {osGroups.map((group:any) => {
                  const isActive = group.key === selectedOsGroup?.key

                  return (
                    <OsCard
                      key={group.key}
                      type='button'
                      active={isActive}
                      onClick={() => handleChange('os')(group.sources[0]?.value)}
                    >
                      {isActive && <SelectedCheck />}
                      {group.icon && <OsIcon src={group.icon}/>}
                      <OsName>{group.title}</OsName>
                    </OsCard>
                  )
                })}
              </OsGrid>

              {selectedOsVersions.length > 0 &&
                <Select
                  toolTip={false}
                  data={selectedOsVersions}
                  selectedValue={values['os']}
                  onChange={(id:any) => handleChange('os')(id)}
                  title={t("VERSION")}
                  fieldName='os'
                  customStyles={{width:'360px'}}
                  {...formikProps}
                />
              }
            </>
          }

          {sourceTab === 'client' &&
            <Select
              required
              toolTip={false}
              data={clientBootSource}
              selectedValue={values['os']}
              onChange={(id:any) => handleChange('os')(id)}
              title={t("CLIENT_IMAGES")}
              fieldName='os'
              customStyles={{width:'360px'}}
              {...formikProps}
            />
          }

          {sourceTab !== 'client' && touched['os'] && errors['os'] &&
            <FieldError>{`${errors['os']}`}</FieldError>
          }

          <HiddenMarketplace>
            <MarketplaceMockGrid>
              <MarketplaceMockCard>
                <FlavorName>WordPress</FlavorName>
                <FlavorMeta>Ubuntu 22.04 LTS</FlavorMeta>
              </MarketplaceMockCard>
              <MarketplaceMockCard>
                <FlavorName>PostgreSQL</FlavorName>
                <FlavorMeta>Debian 12</FlavorMeta>
              </MarketplaceMockCard>
              <MarketplaceMockCard>
                <FlavorName>Nginx</FlavorName>
                <FlavorMeta>Ubuntu 22.04 LTS</FlavorMeta>
              </MarketplaceMockCard>
            </MarketplaceMockGrid>
          </HiddenMarketplace>
        </FieldGroup>

        <PasswordWrapper>
          <Input
            required
            title={t('ROOT_PASSWORD_LABEL')}
            fieldName='root_password'
            placeholder={t('ROOT_PASSWORD_LABEL')}
            onFocus={() => setActiveConfirm(true)}
            onFocusOut={() => setActiveConfirm(false)}
            onBlur={handleBlur}
            autoComplete='new-password'
            ref={InputRef}
            isPassword
            {...formikProps}
          />
          <MatchPassword
            setEnabled={setActiveButton}
            validateString={values['root_password']}
            active={activeConfirm}
          />
        </PasswordWrapper>

        <ControlsRow>
          <Select
            toolTip={false}
            data={storageType}
            selectedValue={values['storageType']}
            onChange={(id:any) => handleChange('storageType')(id)}
            title={t("STORAGE_TYPE")}
            test_id={'instance-storage-select'}
            fieldName='storageType'
            customStyles={{width:'360px'}}
            {...formikProps}
          />
        </ControlsRow>

        <ControlsRow>
          <Counter
            title={t('NUMBER_OF_MACHINES_TO_CREATE')}
            value={values['countMachines']}
            minCount={1}
            maxCount={maxCountInstances(quotas)}
            onChange={(v:any) => {
              handleChange('countMachines')(v.toString())
            }}
            fieldName='countMachines'
            {...formikProps}
          />

          {values['storageType'] !== 'local' &&
            <Counter
              title={`${t('STORAGE')}`}
              value={values['volumeSize']}
              fieldName='volumeSize'
              toolTip={true}
              tooltipText={`${t("TOOLTIP_STORAGE")}`}
              minCount={flavors.find((flav:any) => flav.id === typeValid)?.root_gb || 10}
              hideItems={'GB'}
              {...formikProps}
              onChange={(v:any) => {
                handleChange('volumeSize')(v.toString())}}
            />
          }
        </ControlsRow>

        <InputsWrapper>
          <MultiInput
            toolTip={true}
            toolTipText={`${t("INFO_CREATE_TAGS")}`}
            customStyles={{paddingLeft:'35px'}}
            placeholder={t("ADD_A_TAG")}
            fieldName='tag'
            tags={tags}
            icon={icons.IconTag}
            title={t("TAGS")}
            changeTags={changeTags}
            {...formikProps}
          />
        </InputsWrapper>
      </FormWrapper>

      {maxCountInstances(quotas) < values['countMachines'] &&
        <ErrorContainer isError={true}>
          <img src={ErrorIc}/>
          <ErrorText>{t("ERROR_INSTANCE_CREATE_QUOTAS")}</ErrorText>
        </ErrorContainer>
      }
      <ButtonRow>
        <Button
          variant="primary"
          size="display"
          disabled={maxCountInstances(quotas) < values['countMachines']}
          onClick={handleSubmit}
          focusOnValidationError
          title={t("CONTINUE")}
        />
        <Button
          variant="stroke"
          size="display"
          onClick={openModal}
          title={t("CANCEL")}
        />
      </ButtonRow>
      <ModalVariables
        modalType='unsaved'
        closeModalCancel={closeModalCancel}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
      />
    </BodyForm>
  )
}

export default Configure
